import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { PageMeta } from '@/components/PageMeta';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Trophy, Crown, ArrowRight, Check, Sparkles } from 'lucide-react';

type Tool = {
  rank: number;
  name: string;
  bestFor: string;
  price: string;
  pros: string[];
  cons: string[];
  href: string;
  ctaLabel: string;
  highlight?: boolean;
};

const tools: Tool[] = [
  {
    rank: 1,
    name: 'ListBright',
    bestFor: 'Agents and owners who want every listing asset from one upload',
    price: '$15 one-time · $30/mo',
    pros: [
      'Generates photos, staging, MLS-ready copy, video, microsite + flyer in ~10 min',
      '5 description formats × 28 languages saved per listing',
      'Fixed per-listing price — no per-image fees',
    ],
    cons: ['Newer brand than legacy stagers'],
    href: '/auth',
    ctaLabel: 'Try ListBright free',
    highlight: true,
  },
  {
    rank: 2,
    name: 'BoxBrownie',
    bestFor: 'Brokerages that only need photo retouching + virtual staging',
    price: '~$24/photo (staging)',
    pros: ['Strong staging quality', 'Human-in-the-loop QA'],
    cons: ['No descriptions, video, flyer or microsite', 'Per-image pricing adds up fast'],
    href: '/compare/boxbrownie',
    ctaLabel: 'ListBright vs BoxBrownie',
  },
  {
    rank: 3,
    name: 'Virtual Staging AI',
    bestFor: 'Cheap, fast virtual staging only',
    price: 'From $16/mo',
    pros: ['Affordable', 'Good for high-volume staging'],
    cons: ['Staging only', 'No copy, video, microsite'],
    href: '/compare/virtual-staging-ai',
    ctaLabel: 'ListBright vs Virtual Staging AI',
  },
  {
    rank: 4,
    name: 'Collov AI',
    bestFor: 'Interior-design previews and renders',
    price: 'From $19/mo',
    pros: ['Design-style variety', 'Decent free tier'],
    cons: ['Render-only — no listing copy, video or flyer'],
    href: '/compare/collov-ai',
    ctaLabel: 'ListBright vs Collov',
  },
  {
    rank: 5,
    name: 'ChatGPT (Plus)',
    bestFor: 'Manual one-off description drafts',
    price: '$20/mo',
    pros: ['Flexible writing', 'Wide language support'],
    cons: [
      'Re-prompt for every platform (MLS, FB, IG, Marketplace)',
      'No photos, staging, video, flyer or microsite',
    ],
    href: '/ai-real-estate-descriptions',
    ctaLabel: 'Use our free description AI',
  },
  {
    rank: 6,
    name: 'Styldod',
    bestFor: 'Outsourced staging with human edits',
    price: '~$16/photo',
    pros: ['Human-edited staging'],
    cons: ['Slow turnaround vs AI', 'Staging only'],
    href: '/compare/styldod',
    ctaLabel: 'ListBright vs Styldod',
  },
  {
    rank: 7,
    name: 'ApplyDesign',
    bestFor: 'Self-serve staging templates',
    price: '~$7/photo',
    pros: ['Cheap per-photo'],
    cons: ['Limited styles', 'No other listing assets'],
    href: '/compare/applydesign',
    ctaLabel: 'ListBright vs ApplyDesign',
  },
];

const faqs = [
  {
    q: 'What is the best AI for real estate listings in 2026?',
    a: 'For agents and owners who want every listing asset (photos, staging, descriptions, video, microsite and flyer) from one upload at a fixed price, ListBright is the strongest all-in-one. BoxBrownie and Virtual Staging AI lead if you only need staging.',
  },
  {
    q: 'How much does an AI listing tool cost?',
    a: 'Per-asset AI tools start around $7–$24 per photo for staging, plus $20/mo for description AI — a single listing easily exceeds $80. All-in-one platforms like ListBright start at $15 for an entire listing kit.',
  },
  {
    q: 'Will MLS accept AI-generated photos and descriptions?',
    a: 'Yes for descriptions across every major MLS. AI-enhanced photos are accepted when they faithfully represent the property; AI virtual staging must be disclosed in the listing comments per NAR guidance.',
  },
  {
    q: 'Can AI write descriptions in multiple languages?',
    a: 'ListBright outputs descriptions in 28+ languages per listing automatically. General LLMs can translate, but you have to re-prompt and save each one manually.',
  },
];

export default function BestAiForRealEstateListings() {
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: tools.map((t) => ({
        '@type': 'ListItem',
        position: t.rank,
        name: t.name,
        description: t.bestFor,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  return (
    <Layout>
      <PageMeta
        title="Best AI for Real Estate Listings (2026): 7 Tools Ranked"
        description="The best AI for real estate listings in 2026 — 7 tools ranked by price, output and what each one actually does for agents and owners."
        canonical="https://listbright.ai/best-ai-for-real-estate-listings"
        breadcrumbs={[
          { label: 'Compare', path: '/compare' },
          { label: 'Best AI for Real Estate', path: '/best-ai-for-real-estate' },
          { label: 'Best AI for Listings', path: '/best-ai-for-real-estate-listings' },
        ]}
        jsonLd={jsonLd}
      />

      <section className="bg-gradient-to-br from-primary/5 via-background to-accent/5 py-16 lg:py-24">
        <div className="container max-w-4xl mx-auto px-4 text-center">
          <Badge variant="secondary" className="mb-4 px-4 py-2">
            <Trophy className="w-4 h-4 mr-2" />
            Updated June 2026
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold mb-6 leading-tight">
            Best AI for <span className="text-gradient">Real Estate Listings</span> (2026)
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
            Seven tools ranked by what they actually produce for a listing — not by who shouts loudest.
            Most "AI listing tools" only do one job; only one platform does them all.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button asChild size="lg">
              <Link to="/auth">Try the #1 listing AI free <ArrowRight className="w-4 h-4 ml-2" /></Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/best-ai-for-real-estate">See the full real-estate AI ranking</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-12 border-y border-border/40 bg-muted/30">
        <div className="container max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-display font-bold mb-6 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" /> The short answer
          </h2>
          <ol className="space-y-2 text-base">
            <li><strong>1. ListBright</strong> — best all-in-one listing AI ($15/listing).</li>
            <li><strong>2. BoxBrownie</strong> — best premium photo + staging service.</li>
            <li><strong>3. Virtual Staging AI</strong> — cheapest pure-AI staging.</li>
            <li><strong>4. Collov AI</strong> — best for interior renders.</li>
            <li><strong>5. ChatGPT Plus</strong> — best for manual writing only.</li>
          </ol>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="container max-w-5xl mx-auto px-4 space-y-6">
          {tools.map((t) => (
            <article
              key={t.rank}
              className={`rounded-2xl border p-6 md:p-8 ${
                t.highlight ? 'border-primary/40 bg-primary/5 shadow-lg' : 'border-border/60 bg-card'
              }`}
            >
              <div className="flex flex-wrap items-start gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 font-bold text-primary">
                  #{t.rank}
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-2xl md:text-3xl font-display font-bold mb-1">{t.name}</h2>
                  <div className="text-sm text-muted-foreground">{t.bestFor} · {t.price}</div>
                </div>
                {t.highlight && (
                  <Badge className="bg-primary text-primary-foreground">
                    <Crown className="w-3.5 h-3.5 mr-1.5" /> Editor's pick
                  </Badge>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mb-4 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Pros</div>
                  <ul className="space-y-1">
                    {t.pros.map((p) => (
                      <li key={p} className="flex items-start gap-2"><Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />{p}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Cons</div>
                  <ul className="space-y-1 text-muted-foreground">
                    {t.cons.map((c) => <li key={c}>– {c}</li>)}
                  </ul>
                </div>
              </div>
              <Button asChild size="sm" variant={t.highlight ? 'default' : 'outline'}>
                <Link to={t.href}>{t.ctaLabel} <ArrowRight className="w-3.5 h-3.5 ml-1.5" /></Link>
              </Button>
            </article>
          ))}
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-muted/30 border-y border-border/40">
        <div className="container max-w-3xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-8 text-center">FAQ</h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`}>
                <AccordionTrigger className="text-left text-base font-semibold">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-16">
        <div className="container max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-display font-bold mb-4">Keep exploring</h2>
          <div className="flex flex-wrap justify-center gap-3">
            <Button asChild variant="outline"><Link to="/best-ai-for-real-estate">Best AI for Real Estate</Link></Button>
            <Button asChild variant="outline"><Link to="/best-virtual-staging-software">Best Virtual Staging Software</Link></Button>
            <Button asChild variant="outline"><Link to="/ai-listing-generator">AI Listing Generator</Link></Button>
            <Button asChild variant="outline"><Link to="/how-to-use-ai-in-real-estate">How to use AI in real estate</Link></Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}